import { useMutation, useQuery, useQueryClient } from 'react-query'
import type {
  AgentOption,
  CaApiClient,
  CaBackend,
  ConversationMeta,
} from '../../shared/types'

export interface CreateConversationVars {
  backend?: CaBackend
  agent?: AgentOption
  dashboardId?: string
  title?: string
}

export interface UseConversationsOpts {
  client: CaApiClient
  dashboardId?: string
  enabled: boolean
  onCreated?: (c: ConversationMeta) => void
  onCreateError?: (e: unknown) => void
  onRenamed?: (c: ConversationMeta) => void
}

export function useConversations(opts: UseConversationsOpts) {
  const { client, dashboardId, enabled, onCreated, onCreateError, onRenamed } = opts
  const qc = useQueryClient()
  const key = ['ca', 'conversations', dashboardId ?? 'all']

  const list = useQuery(
    key,
    async () => {
      const rows = await client.listConversations(dashboardId)
      // eslint-disable-next-line no-console
      console.log(
        '[ca:list] fetch scope=%s → %d rows: %s',
        dashboardId ?? 'all',
        rows.length,
        rows.map((r) => `${r.id.slice(0, 8)}${r.title ? `(${r.title})` : ''}`).join(', '),
      )
      return rows
    },
    { enabled, refetchOnWindowFocus: false },
  )

  const applyLocal = (
    label: string,
    update: (rows: ConversationMeta[]) => ConversationMeta[],
  ) => {
    qc.setQueryData<ConversationMeta[]>(key, (prev) => update(prev ?? []))
    // eslint-disable-next-line no-console
    console.log(
      '[ca:list] %s → cache[%s] now %d rows',
      label,
      String(key[2]),
      (qc.getQueryData<ConversationMeta[]>(key) ?? []).length,
    )
    qc.invalidateQueries(key)
  }

  const create = useMutation(
    (vars: CreateConversationVars) =>
      client.createConversation(vars.title, {
        backend: vars.backend,
        agentId: vars.agent?.id,
        dashboardId: vars.dashboardId,
      }),
    {
      onSuccess: (c) => {
        applyLocal(`created ${c.id.slice(0, 8)}`, (rows) => [
          c,
          ...rows.filter((r) => r.id !== c.id),
        ])
        onCreated?.(c)
      },
      onError: (e) => onCreateError?.(e),
    },
  )

  const rename = useMutation(
    ({ id, title }: { id: string; title: string }) =>
      client.renameConversation(id, title),
    {
      onMutate: async ({ id, title }) => {
        await qc.cancelQueries(key)
        const previous = qc.getQueryData<ConversationMeta[]>(key)
        qc.setQueryData<ConversationMeta[]>(key, (rows) =>
          (rows ?? []).map((r) => (r.id === id ? { ...r, title } : r)),
        )
        return { previous }
      },
      onError: (_e, _vars, context) => {
        const ctx = context as { previous?: ConversationMeta[] } | undefined
        if (ctx?.previous) {
          qc.setQueryData(key, ctx.previous)
        }
      },
      onSuccess: (c) => onRenamed?.(c),
      onSettled: () => qc.invalidateQueries(key),
    },
  )

  const dropLocal = (id: string) => {
    applyLocal(`deleted ${id.slice(0, 8)}`, (rows) => rows.filter((r) => r.id !== id))
  }

  return { key, list, create, rename, dropLocal }
}
